import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { products } from '../assets/productsData';
import './Products.css';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.07 } }),
};

const categories = [
  { key: 'all', label: 'All Products' },
  { key: 'frozen', label: '🥶 Frozen Foods' },
  { key: 'spices', label: '🌶️ Organic Spices' },
  { key: 'foxnuts', label: '🍿 Foxnuts' },
  { key: 'dehydrated', label: '🧅 Dehydrated Powders' },
];

const Products = () => {
  const [searchParams] = useSearchParams();
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    const cat = searchParams.get('cat');
    const id = searchParams.get('id');
    if (cat) setActiveFilter(cat);
    if (id) {
      const p = products.find(x => x.id === parseInt(id));
      if (p) setSelectedProduct(p);
    }
  }, [searchParams]);

  useEffect(() => {
    document.body.style.overflow = selectedProduct ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [selectedProduct]);

  const filtered = activeFilter === 'all' ? products : products.filter(p => p.cat === activeFilter);

  return (
    <main className="products-page">
      {/* HERO BANNER */}
      <section className="products-hero">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="section-tag" style={{ background: 'rgba(255,255,255,0.15)', color: 'var(--gold-light)' }}>
              Our Products
            </div>
            <h1 className="section-title" style={{ color: 'var(--white)', fontSize: 'clamp(30px,5vw,52px)' }}>
              Complete Product Catalog
            </h1>
            <p className="section-sub" style={{ color: 'rgba(255,255,255,0.75)' }}>
              Premium quality agro products packaged to international export standards.
              MOQ 500kg across all categories.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FILTERS + GRID */}
      <section className="products-main">
        <div className="container">
          <div className="filter-bar">
            {categories.map(c => (
              <button
                key={c.key}
                className={`filter-btn ${activeFilter === c.key ? 'active' : ''}`}
                onClick={() => setActiveFilter(c.key)}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className="products-count">
            Showing {filtered.length} product{filtered.length !== 1 ? 's' : ''}
            {activeFilter !== 'all' ? ` in ${categories.find(c => c.key === activeFilter)?.label}` : ''}
          </div>

          <div className="products-grid">
            <AnimatePresence>
              {filtered.map((p, i) => (
                <motion.div
                  key={p.id}
                  className="product-card"
                  layout
                  initial="hidden"
                  animate="visible"
                  exit={{ opacity: 0, scale: 0.95 }}
                  custom={i * 0.05}
                  variants={fadeUp}
                  onClick={() => setSelectedProduct(p)}
                  style={{ '--card-bg': p.bgColor }}
                >
                  {/* Changed emoji container to standard img rendering tag */}
                  <div className="product-card-img">
                    <img src={p.image} alt={p.name} className="product-grid-img" />
                  </div>
                  <div className="product-card-body">
                    <div className="product-cat">{p.catLabel}</div>
                    <div className="product-name">{p.name}</div>
                    <div className="product-desc">{p.desc.substring(0, 100)}...</div>
                    <div className="product-meta">
                      <span className="product-moq">MOQ: {p.moq}</span>
                      <div className="product-arrow">→</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* PRODUCT MODAL */}
      <AnimatePresence>
        {selectedProduct && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={e => { if (e.target === e.currentTarget) setSelectedProduct(null); }}
          >
            <motion.div
              className="modal"
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="modal-hero" style={{ background: selectedProduct.bgColor }}>
                <button className="modal-close" onClick={() => setSelectedProduct(null)}>✕</button>
                {/* Replaced emoji text inside modal head banner */}
                <img src={selectedProduct.image} alt={selectedProduct.name} className="modal-product-img" />
              </div>
              <div className="modal-body">
                <div className="modal-cat">{selectedProduct.catLabel}</div>
                <h2 className="modal-title">{selectedProduct.name}</h2>
                <p className="modal-desc">{selectedProduct.longDesc}</p>

                <div className="modal-grid">
                  <div className="modal-info-box">
                    <div className="modal-info-label">Minimum Order Qty</div>
                    <div className="modal-info-value">{selectedProduct.moq}</div>
                  </div>
                  <div className="modal-info-box">
                    <div className="modal-info-label">Grade / Quality</div>
                    <div className="modal-info-value">{selectedProduct.grade}</div>
                  </div>
                </div>

                {selectedProduct.specs && (
                  <div className="modal-specs">
                    <div className="modal-info-label" style={{ marginBottom: '8px' }}>Specifications</div>
                    <p style={{ fontSize: '13px', color: 'var(--text-mid)', lineHeight: '1.6' }}>
                      {selectedProduct.specs}
                    </p>
                  </div>
                )}

                <div style={{ marginTop: '20px' }}>
                  <div className="modal-pkg-title">Export Packaging Options</div>
                  <div className="pkg-list">
                    {selectedProduct.pkg.map(pk => (
                      <span key={pk} className="pkg-badge">{pk}</span>
                    ))}
                  </div>
                </div>

                <a
                  href={`/contact?product=${encodeURIComponent(selectedProduct.name)}`}
                  className="modal-inquiry-btn"
                >
                  Send Inquiry for {selectedProduct.name} →
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default Products;